import classes from './styles.module.scss';

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RELATIVE_TYPES } from '../../core/consts';
import { get, post, postWithFile } from '../../core/client';
import ReCAPTCHA from 'react-google-recaptcha';
import { GetSplitPhoneNumber, IsNullOrEmpty, IsOnlyLetters, IsValidEmail, IsValidTckn } from '../../core/utility';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';
import InfoIcon from '@mui/icons-material/Info';
import FormControlLabel from '@mui/material/FormControlLabel';
import { CustomButton } from '../../components/buttons';
import Input from '../../components/inputs/text-field-input';
import Select from '../../components/inputs/select';
import DatePicker from '../../components/inputs/date-picker';
import SmsCodeModal from '../shared/SmsCodeModal';
import PhoneInput from '../../components/inputs/phone';
import UploadInput from '../../components/inputs/upload-input';
import { ResponseWithSuccess } from '../../core/client/Models';
import JoinFormV2ContextProvider, { JoinFormV2Context, LegalContractConsentType } from '../shared/JoinFormV2Context';
import reactStringReplace from 'react-string-replace';
import DetailedInfo from '../shared/DetailedInfo';
import Typography from '@mui/material/Typography';
import WarningModal from '../../components/warning-modal';

interface StateModel {
    memberRelativeId: number;
    relativeFullName: string;
    orgMemberFullName: string;
    relationTypeId: number;
    relationTypeName: string;
    tcIdentityNo: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    birthDate: string;
    childGender: any;
    hasRelativeFile: boolean;
    file: File | null;
    fileName: string;
    reCaptchaToken: string;
    //ileti izni
    commPerm: boolean;
    //kişisel veri paylaşım izni
    approvedConsents: Array<string>;
}

interface InputErrorModel {
    name: string;
    errorText: string;
}

interface RelativeInfo {
    memberRelativeId: number;
    orgMemberTcIdentityNo: string;
    orgMemberFullName: string;
    orgMemberCompanyId: number;
    tcIdentityNo: string;
    firstName: string;
    lastName: string;
    relationTypeId: number;
    relationTypeName: string;
    birthDate: string;
    childGender?: string;
    isProcessed: boolean;
    email: string | null;
    phone: string | null;
    hasRelativeFile: boolean;
    createDate: string;
}

interface CompleteRelativeMembershipResponse extends ResponseWithSuccess {
    isSendToSystem: boolean;
}

const initialState: StateModel = {
    memberRelativeId: 0,
    orgMemberFullName: '',
    relativeFullName: '',
    relationTypeId: 0,
    relationTypeName: '',
    tcIdentityNo: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    birthDate: '',
    commPerm: false,
    childGender: null,
    file: null,
    fileName: '',
    hasRelativeFile: false,
    reCaptchaToken: '',
    approvedConsents: [],
};

const genders = [
    { id: 0, name: 'Tanımsız' },
    { id: 1, name: 'Erkek' },
    { id: 2, name: 'Kadın' },
];

const useQuery = () => {
    const { search } = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
};

const RelativeJoinFormComponent = () => {
    const { legalTextLoading, legalTexts, loadLegalTexts, openLegalTextModal } = useContext(JoinFormV2Context);
    let query = useQuery();
    const relativeGuid = query.get('rg');
    const navigate = useNavigate();

    //states
    const [state, setState] = useState<StateModel>(initialState);
    const [loading, setLoading] = useState(false);
    const [loadingSendForm, setLoadingSendForm] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [initialErrorText, setInitialErrorText] = useState('');
    const [inputErrors, setInputErrors] = useState<Array<InputErrorModel>>([]);
    const [isKvkkError, setIsKvkkError] = useState(false);
    const [showSmsCodeModal, setShowSmsCodeModal] = useState(false);

    const [openPopup, setOpenPopup] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');

    const handleWarnigOpen = (msg: string) => {
        setMessage(msg);
        setOpenPopup(true);
    };

    const handleWarningClose = () => setOpenPopup(false);

    const recaptchaRef = useRef<any>(null);

    useEffect(() => {
        const initialLoad = async () => {
            setLoading(true);
            if (relativeGuid && !IsNullOrEmpty(relativeGuid)) {
                const relativeInfoData = await loadRelativeInfo(relativeGuid);
                await checkMemberAndMemberRequestExistance(relativeInfoData);
                if (relativeInfoData) {
                    const relativeInfo = relativeInfoData.relativeInfo;
                    let splittedPhone = GetSplitPhoneNumber(relativeInfo.phone);

                    setState(prev => ({
                        ...prev,
                        memberRelativeId: relativeInfo.memberRelativeId,
                        orgMemberFullName: relativeInfo.orgMemberFullName,
                        relativeFullName: `${relativeInfo.firstName} ${relativeInfo.lastName}`,
                        tcIdentityNo: relativeInfo.tcIdentityNo,
                        firstName: relativeInfo.firstName,
                        lastName: relativeInfo.lastName,
                        birthDate: relativeInfo.birthDate,
                        relationTypeId: relativeInfo.relationTypeId,
                        relationTypeName: relativeInfo.relationTypeName,
                        childGender: relativeInfo.childGender,
                        email: relativeInfo.email ?? '',
                        phone: splittedPhone,
                        hasRelativeFile: relativeInfo.hasRelativeFile,
                    }));
                }
                loadLegalTexts([
                    LegalContractConsentType.VERI_IZNI,
                    LegalContractConsentType.YKB_VERI_AKTARIM_IZNI,
                    LegalContractConsentType.ETK_IZNI,
                    LegalContractConsentType.YURT_DISI_VERI_IZNI,
                    LegalContractConsentType.UYELIK_SOZLESMESI,
                    LegalContractConsentType.YKB_AYDINLATMA,
                ]);
            }
            setLoading(false);
        };
        initialLoad();
    }, [relativeGuid]);

    //methods
    const loadRelativeInfo = async (guid: string) => {
        const { data, fail, errorMessage } = await get<{ relativeInfo: RelativeInfo }>(`api/membership/get-relative-info/${guid}`);
        if (fail) {
            setInitialErrorText(prev => errorMessage);
        }

        return data;
    };

    const checkMemberAndMemberRequestExistance = async (relativeInfoData: RelativeInfo | any) => {
        if (!relativeInfoData) {
            return;
        }
        const relativeInfo = relativeInfoData.relativeInfo;
        const request = {
            tcIdentityNo: relativeInfo.tcIdentityNo,
            mobilePhone: relativeInfo.phone,
        };
        const { data, fail, errorMessage } = await post<{ isExist: boolean }>(`api/membership/membership-existance`, request);
        if (fail) {
            if (relativeInfo.relationTypeId === 0) {
                navigate('/join-form/congrats');
            }
            navigateRelativeCongrastsPage(relativeInfo);
        }
    };

    const isConsentApproved = (type: string) => {
        return state.approvedConsents.some(consent => consent === type);
    };

    const handleChange = (e: any) => {
        setState(prev => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handleFileInput = (files: FileList | []) => {
        setState(prevState => ({
            ...prevState,
            file: files.length > 0 ? files[0] : null,
            fileName: files.length > 0 ? files[0].name : '',
        }));
    };
    const validateFileInput = (files: File[]) => {
        let fileTypes: Array<string> = ['application/pdf'];
        let maxSize: number = 500000; //0.5mb
        if (files.length > 0) {
            for (let i = 0; i < files.length; i++) {
                if (!fileTypes.includes(files[i].type)) {
                    const message =
                        'İşleminizin tamamlanabilmesi için E-devlet den alacağınız vukuatlı nüfus kayıt örneğini PDF olarak yüklemeniz gerekmektedir. PDF olarak yüklenmediği için işleme devam edilememektedir. Lütfen doğru formatta dosya yüklemeyi tekrar deneyiniz.';
                    return message;
                }
                if (files[i].size > maxSize) {
                    let currentName: string = files[i].name;
                    const message = 'Dosya boyutu max 0.5 mb olabilir.' + currentName;
                    return message;
                }
            }
        }

        return null;
    };

    const onChangeCaptcha = async (e: any) => {
        setState(prev => ({
            ...prev,
            reCaptchaToken: e,
        }));
    };

    const toggleSmsModal = (show: boolean) => {
        setShowSmsCodeModal(show);
    };

    const handleMembershipAggreement = () => {
        openLegalTextModal(LegalContractConsentType.UYELIK_SOZLESMESI);
    };
    const handleYkbAggreement = () => {
        openLegalTextModal(LegalContractConsentType.YKB_AYDINLATMA);
    };

    const handleConsentCheckbox = (e: any) => {
        const consentType = e.target.name;
        let consents: Array<string> = [...state.approvedConsents];
        consents = consents.filter(x => x !== consentType);

        if (e.target.checked) {
            consents = [...consents, consentType];

            if (
                consentType === LegalContractConsentType.VERI_IZNI ||
                consentType === LegalContractConsentType.YURT_DISI_VERI_IZNI ||
                consentType === LegalContractConsentType.VAKIF_DERNEK_IZNI
            ) {
                openLegalTextModal(consentType as LegalContractConsentType);
            }
        }
        setState(prev => ({
            ...prev,
            approvedConsents: consents,
        }));
    };

    const isRequiredConsentsChecked = (model: StateModel) => {
        const requiredConsents = legalTexts?.consents.filter(
            x => x.required && x.type !== LegalContractConsentType.UYELIK_SOZLESMESI && x.type !== LegalContractConsentType.YKB_AYDINLATMA,
        );
        if (requiredConsents) {
            for (let item of requiredConsents) {
                if (!isConsentApproved(item.type)) {
                    return;
                }
            }
        }
        return true;
    };

    const setError = (name: keyof StateModel, errorText = 'Zorunlu alan') => {
        const error: InputErrorModel = { name, errorText };
        setInputErrors((prev: Array<InputErrorModel>) => [...prev, error]);
    };

    const validate = (model: StateModel): boolean => {
        setIsKvkkError(false);
        setInputErrors([]);

        let isValid = true;

        if (!model.hasRelativeFile) {
            if (model.file === null) {
                setError('file', 'Geçerli bir belge yükleyiniz.');
                isValid = false;
            } else {
                const fileInputValidation = validateFileInput([model.file]);
                if (fileInputValidation) {
                    setError('file', fileInputValidation);
                    isValid = false;
                }
            }
        }

        if (!IsValidTckn(model.tcIdentityNo)) {
            setError('tcIdentityNo', 'Geçersiz TC kimlik no');
            isValid = false;
        }
        if (IsNullOrEmpty(model.firstName) || !IsOnlyLetters(model.firstName)) {
            setError('firstName', 'Geçersiz ad');
            isValid = false;
        }
        if (IsNullOrEmpty(model.lastName) || !IsOnlyLetters(model.lastName)) {
            setError('lastName', 'Geçersiz soyad');
            isValid = false;
        }
        if (IsNullOrEmpty(model.birthDate)) {
            setError('birthDate', 'Geçersiz doğum tarihi');
            isValid = false;
        }
        if (IsNullOrEmpty(model.phone) || model.phone[0] !== '5' || model.phone.length !== 10) {
            setError('phone', 'Geçersiz telefon');
            isValid = false;
        }
        if (!IsValidEmail(model.email)) {
            setError('email', 'Geçersiz e-posta');
            isValid = false;
        }
        if (!isRequiredConsentsChecked(model)) {
            setIsKvkkError(true);
            isValid = false;
        }

        if (isValid && IsNullOrEmpty(model.reCaptchaToken)) {
            setError('reCaptchaToken', 'Robot doğrulamasını yapmalısınız.');
            isValid = false;
        }

        return isValid;
    };

    const handleSend = async (model: StateModel) => {
        if (!validate(model)) return;

        setLoadingSendForm(true);
        const request = {
            memberRelativeId: model.memberRelativeId,
            relationTypeId: model.relationTypeId,
            tcIdentityNo: model.tcIdentityNo,
            firstName: model.firstName,
            lastName: model.lastName,
            birthDate: model.birthDate,
            phone: '90' + model.phone,
            email: model.email,
            childGender: model.childGender,
            isFileUploaded: model.file !== null,
            reCaptchaToken: model.reCaptchaToken,
        };
        const { data, fail, errorMessage } = await post<ResponseWithSuccess>('api/membership/validate-relative-form', request);
        setLoadingSendForm(false);
        if (data && data.success) {
            toggleSmsModal(true);
        }

        if (fail) {
            setErrorText(errorMessage);
        }

        (window as any).grecaptcha.reset();
    };

    const handleSmsValidated = async (code: string, model: StateModel) => {
        setErrorText('');
        setLoadingSendForm(true);
        const request = {
            smsCode: code,
            memberRelativeId: model.memberRelativeId,
            relationTypeId: model.relationTypeId,
            tcIdentityNo: model.tcIdentityNo,
            firstName: model.firstName,
            lastName: model.lastName,
            birthDate: model.birthDate,
            phone: '90' + model.phone,
            email: model.email,
            childGender: model.childGender,
            isApprovedVeriIzni: isConsentApproved(LegalContractConsentType.VERI_IZNI),
            isApprovedYkbAktarim: isConsentApproved(LegalContractConsentType.YKB_VERI_AKTARIM_IZNI),
            commPermStatus: isConsentApproved(LegalContractConsentType.ETK_IZNI),
            hasRelativeFile: model.hasRelativeFile,
            file: model.file,
            fileName: model.fileName,
        };
        const { data, fail, errorMessage } = await postWithFile<CompleteRelativeMembershipResponse>('api/membership/complete-relative-form', request);
        setLoadingSendForm(false);
        if (data && data.success) {
            if (data.isSendToSystem) {
                navigate('/join-form/congrats');
            }
            navigateRelativeCongrastsPage(model);
        } else {
            handleWarnigOpen('İşleminiz tamamlanamadı. Bilgi almak için 444 0 447 numaralı KoçAilem Çağrı Merkezi ile iletişime geçebilirsiniz. ');
            return;
        }

        if (fail) {
            setErrorText(errorMessage);
        }
    };

    const navigateRelativeCongrastsPage = (relativeInfo: StateModel) => {
        const relationTypeId = relativeInfo.relationTypeId;
        if (relationTypeId === RELATIVE_TYPES.Anne || relationTypeId === RELATIVE_TYPES.Baba) {
            navigate('/join-form/congrats-parents', { state: { parentName: `${relativeInfo.firstName} ${relativeInfo.lastName}` } });
        }
        if (relationTypeId === RELATIVE_TYPES.Es) {
            navigate('/join-form/congrats-spouse', { state: { parentName: `${relativeInfo.firstName} ${relativeInfo.lastName}` } });
        }
        if (relationTypeId === RELATIVE_TYPES.Cocuk || relationTypeId === RELATIVE_TYPES.Kardes) {
            navigate('/join-form/congrats-child', { state: { parentName: `${relativeInfo.firstName} ${relativeInfo.lastName}` } });
        }
    };

    return (
        <div className={classes.relativeJoinForm}>
            <div className={classes.overlay}>
                <div className={classes.container}>
                    <div className={classes.form}>
                        <div className={classes.text}>
                            {initialErrorText && (
                                <Box marginBottom={2}>
                                    <Alert severity="error">{initialErrorText}</Alert>
                                </Box>
                            )}
                            <p>
                                Sevgili {state.relativeFullName}, {state.orgMemberFullName} Seni KoçAilemli olmaya davet etti.
                            </p>
                            <p>Aşağıdaki formu doldurarak üyeliğini hemen başlatabilirsin.</p>
                            <p>KoçAilem'e şimdiden hoş geldin!</p>
                        </div>
                        <h1>AİLE YAKINI DAVET FORMU</h1>
                        <Grid container spacing={2}>
                            <Grid item sm={6} xs={12}>
                                <Input
                                    id="relationTypeName"
                                    name="relationTypeName"
                                    caption="Yakınlık Tipi"
                                    disabled
                                    value={state.relationTypeName}
                                />
                                <input id="relationTypeId" name="relationTypeId" value={state.relationTypeId} hidden />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                                <Input
                                    id="tcIdentityNo"
                                    name="tcIdentityNo"
                                    caption="TC Kimlik No"
                                    patternFormat={{
                                        format: '###########',
                                    }}
                                    value={state.tcIdentityNo}
                                    onChange={handleChange}
                                    errorText={inputErrors.find(x => x.name === 'tcIdentityNo')?.errorText}
                                />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                                <Input
                                    id="firstName"
                                    name="firstName"
                                    caption="Ad"
                                    toUpperCase
                                    value={state.firstName}
                                    onChange={handleChange}
                                    errorText={inputErrors.find(x => x.name === 'firstName')?.errorText}
                                />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                                <Input
                                    id="lastName"
                                    name="lastName"
                                    caption="Soyad"
                                    toUpperCase
                                    value={state.lastName}
                                    onChange={handleChange}
                                    errorText={inputErrors.find(x => x.name === 'lastName')?.errorText}
                                />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                                <DatePicker
                                    id="birthDate"
                                    name="birthDate"
                                    caption="Doğum Tarihi"
                                    value={state.birthDate}
                                    onChange={handleChange}
                                    errorText={inputErrors.find(x => x.name === 'birthDate')?.errorText}
                                />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                                <PhoneInput
                                    id="phone"
                                    name="phone"
                                    caption="Telefon"
                                    placeholder="(5##) ### ## ##"
                                    format="(###) ### ## ##"
                                    value={state.phone}
                                    onChange={handleChange}
                                    errorText={inputErrors.find(x => x.name === 'phone')?.errorText}
                                />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                                <Input
                                    id="email"
                                    name="email"
                                    caption="Kişisel E-Posta"
                                    value={state.email}
                                    onChange={handleChange}
                                    errorText={inputErrors.find(x => x.name === 'email')?.errorText}
                                />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                                <Select
                                    id="childGender"
                                    name="childGender"
                                    caption="Çocuk Cinsiyeti"
                                    disabled={state.relationTypeId !== 4}
                                    onChange={handleChange}
                                    options={{
                                        data: genders,
                                        displayField: 'name',
                                        displayValue: 'id',
                                        selected: state.childGender,
                                    }}
                                    errorText={inputErrors.find(x => x.name === 'childGender')?.errorText}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                {state.hasRelativeFile ? (
                                    <Box display="flex" marginTop="8px">
                                        <InfoIcon fontSize="small" color="info" />
                                        <Typography variant="body1" color="textSecondary" marginLeft="8px">
                                            Aile yakınına ait doküman mevcuttur.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <>
                                        <p>
                                            E-devlet üzerinden alınacak Nüfus Kayıt Örneği Belgesi menüsünden Nüfus Kayıt Örneği Tipi'nin{' '}
                                            <strong>Aile</strong> seçilmesi gerekmektedir.
                                        </p>
                                        <UploadInput
                                            text="DOSYA SEÇ"
                                            disabled={state.hasRelativeFile}
                                            options={{
                                                accept: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/png,image/jpg,image/jpeg',
                                                onChange: handleFileInput,
                                                multiple: false,
                                            }}
                                            errorText={inputErrors.find(x => x.name === 'file')?.errorText}
                                        />
                                        <Box display="flex" marginTop="8px">
                                            <InfoIcon fontSize="small" color="action" />
                                            <Typography variant="body2" color="textSecondary" ml={0.5}>
                                                Bu alana yükleyeceğiniz dosya yalnızca PDF uzantılı olabilir.
                                            </Typography>
                                        </Box>
                                        <DetailedInfo />
                                    </>
                                )}
                            </Grid>
                        </Grid>
                        <div className="py-3">
                            {legalTextLoading ? (
                                <p>Yükleniyor...</p>
                            ) : (
                                legalTexts?.consents
                                    .filter(
                                        x =>
                                            x.type !== LegalContractConsentType.UYELIK_SOZLESMESI &&
                                            x.type !== LegalContractConsentType.YKB_AYDINLATMA,
                                    )
                                    .map((item, index) => (
                                        <div className="py-1" key={index}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name={item.type}
                                                        color="primary"
                                                        checked={isConsentApproved(item.type)}
                                                        onChange={handleConsentCheckbox}
                                                    />
                                                }
                                                label={
                                                    <div
                                                        className={
                                                            item.required && isKvkkError && !isConsentApproved(item.type)
                                                                ? `${classes.errorCheckbox}`
                                                                : ''
                                                        }
                                                        dangerouslySetInnerHTML={{ __html: item.text }}></div>
                                                }
                                            />
                                        </div>
                                    ))
                            )}
                        </div>
                        <Box display="flex" justifyContent="center" marginBottom={2}>
                            <ReCAPTCHA ref={recaptchaRef} sitekey="6LdqH0QfAAAAAAQR10C9Poo7LItjCQ0Fbe2l9Fnn" onChange={onChangeCaptcha} />
                        </Box>
                        <Box marginBottom={2} textAlign="center">
                            {reactStringReplace(
                                legalTexts?.consents.find(x => x.type === LegalContractConsentType.YKB_AYDINLATMA)?.text ?? '',
                                /<strong>(.*?)<\/strong>/,
                                (match, i) => (
                                    <button style={{ color: '#177897', cursor: 'pointer', border: 'none' }} onClick={handleYkbAggreement}>
                                        <strong>{match}</strong>
                                    </button>
                                ),
                            )}
                        </Box>
                        <Box marginBottom={2} textAlign="center">
                            'KOÇAİLEM ÜYELİĞİMİ TAMAMLA' butonuna basılması ile{' '}
                            <button style={{ color: '#177897', cursor: 'pointer', border: 'none' }} onClick={handleMembershipAggreement}>
                                <strong>Üyelik Sözleşmesi</strong>
                            </button>
                            'ni okuduğunuzu ve kabul ettiğinizi onaylıyorsunuz.
                        </Box>
                        {errorText && (
                            <Box marginBottom={2}>
                                <Alert severity="error">{errorText}</Alert>
                            </Box>
                        )}
                        {inputErrors.find(x => x.name === 'reCaptchaToken')?.errorText && (
                            <Box marginBottom={2}>
                                <Alert severity="error">{inputErrors.find(x => x.name === 'reCaptchaToken')?.errorText}</Alert>
                            </Box>
                        )}
                        {inputErrors.some(x => x.name !== 'reCaptchaToken') && (
                            <Box marginBottom={2}>
                                <Alert severity="error">Zorunlu alanları kontrol ediniz.</Alert>
                            </Box>
                        )}

                        <Box display="flex" justifyContent="center" marginBottom={2}>
                            <CustomButton
                                text="KOÇAİLEM ÜYELİĞİMİ TAMAMLA"
                                className="btn-primary"
                                disabled={loading || loadingSendForm || initialErrorText.length > 0}
                                loading={loadingSendForm}
                                onClick={() => handleSend(state)}
                            />
                        </Box>
                    </div>
                </div>
            </div>
            <SmsCodeModal
                show={showSmsCodeModal}
                phone={state.phone}
                onSend={(val: string) => handleSmsValidated(val, state)}
                onClose={() => toggleSmsModal(false)}
            />
            <WarningModal open={openPopup} message={message} handleClose={() => handleWarningClose()} />
        </div>
    );
};

const RelativeJoinForm = () => (
    <JoinFormV2ContextProvider>
        <RelativeJoinFormComponent />
    </JoinFormV2ContextProvider>
);

export default RelativeJoinForm;
